"use client";

import React from "react";
import {
  Box,
  Flex,
  Container,
  VStack,
  HStack,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
} from "@chakra-ui/react";

/**
 * Layout-matching skeleton for the client profile page. Shown while the base
 * client record is still loading so the user sees the page structure (header +
 * two-column content) immediately instead of a blank full-screen spinner.
 * The real content then replaces it section-by-section as data arrives.
 */
const CardSkeleton: React.FC<{ lines?: number }> = ({ lines = 4 }) => (
  <Box p={6} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
    <Skeleton height="24px" width="55%" mb={5} />
    <SkeletonText noOfLines={lines} spacing="4" skeletonHeight="3" />
  </Box>
);

const ClientProfileSkeleton: React.FC = () => {
  return (
    <Box as="main" bg="gray.50" minH="100vh">
      {/* Header band — mirrors ClientHeader */}
      <Box as="header" bg="gray.100" w="full" py={4} px={6}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={6}>
          <HStack spacing={3} minW="240px">
            <SkeletonCircle size="90px" />
            <VStack align="flex-start" spacing={3}>
              <Skeleton height="24px" width="180px" />
              <Skeleton height="14px" width="120px" />
            </VStack>
          </HStack>
          <Skeleton height="20px" width="20px" />
        </Flex>
      </Box>

      <Container maxW="container.xl" mt={10} pb={10}>
        <Flex direction={{ base: "column", lg: "row" }} gap={8}>
          {/* Left column — EngagementOverview + ClientDetails */}
          <VStack spacing={8} align="stretch" flex={{ base: "1", lg: "0.65" }}>
            <CardSkeleton lines={4} />
            <CardSkeleton lines={6} />
          </VStack>

          {/* Right column — MeetingHistory */}
          <VStack spacing={8} align="stretch" flex={{ base: "1", lg: "0.35" }}>
            <Box p={6} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
              <Skeleton height="24px" width="70%" mb={5} />
              <Skeleton height="44px" mb={3} />
              <Skeleton height="44px" mb={3} />
              <Skeleton height="44px" mb={3} />
              <Skeleton height="44px" />
            </Box>
          </VStack>
        </Flex>
      </Container>
    </Box>
  );
};

export default ClientProfileSkeleton;
